import { defineComponent as _defineComponent } from 'vue'
import { computed, defineAsyncComponent, getCurrentInstance, ref, useSlots } from 'vue';
import { LOCAL_ERROR_SHOW_TIME } from '@white-label-configuration/constants';
import { TranslateResult } from 'vue-i18n';

type CustomErrorMessages = {
  [key: string]: string;
};

interface ErrorObject {
  $error: boolean;
  required?: boolean;
  [key: string]: any;
}

interface TextFieldProps {
  prependIconClass?: string;
  customClasses?: string;
  prependIcon?: string | Function;
  appendIcon?: string;
  appendIconClass?: string;
  maxLength?: number;
  onlyNumbers?: boolean;
  readonly?: boolean;
  type?: string;
  label?: string;
  value?: string | number;
  placeholder?: string;
  error?: ErrorObject;
  errorLabel?: string;
  addRequiredStar?: boolean;
  inputUpperCase?: boolean;
  inputLowerCase?: boolean;
  noPaste?: boolean;
  displayErrorMessage?: boolean;
  clearable?: boolean;
  customErrorMessages?: CustomErrorMessages;
  testId?: string | null;
  disabled?: boolean;
  isSuccess?: boolean;
  id?: string;
  prefix?: string;
  autocomplete?: string;
  tabindex?: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'text-field',
  props: {
    prependIconClass: { default: '' },
    customClasses: { default: '' },
    prependIcon: { type: [String, Function], default: '' },
    appendIcon: { default: '' },
    appendIconClass: { default: '' },
    maxLength: { default: 0 },
    onlyNumbers: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    type: { default: 'text' },
    label: { default: '' },
    value: { default: '' },
    placeholder: { default: '' },
    error: { default: () => ({} as ErrorObject) },
    errorLabel: { default: '' },
    addRequiredStar: { type: Boolean, default: false },
    inputUpperCase: { type: Boolean, default: false },
    inputLowerCase: { type: Boolean, default: false },
    noPaste: { type: Boolean, default: false },
    displayErrorMessage: { type: Boolean, default: true },
    clearable: { type: Boolean, default: false },
    customErrorMessages: { default: () => ({}) },
    testId: { default: null },
    disabled: { type: Boolean, default: false },
    isSuccess: { type: Boolean, default: false },
    id: { default: '' },
    prefix: { default: '' },
    autocomplete: { default: 'off' },
    tabindex: { default: '0' }
  },
  emits: ["input", "clickReadonly", "focus", "blur", "keydown", "paste"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'input', value: string): void;
  (e: 'clickReadonly'): void;
  (e: 'focus'): void;
  (e: 'blur'): void;
  (e: 'keydown', event: KeyboardEvent): void;
  (e: 'paste'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { prependIconClass: string, customClasses: string, prependIcon: string | Function, appendIcon: string, appendIconClass: string, maxLength: number, onlyNumbers: boolean, readonly: boolean, type: string, label: string, value: string | number, placeholder: string, error: ErrorObject, errorLabel: string, addRequiredStar: boolean, inputUpperCase: boolean, inputLowerCase: boolean, noPaste: boolean, displayErrorMessage: boolean, clearable: boolean, customErrorMessages: CustomErrorMessages, testId: string | null, disabled: boolean, isSuccess: boolean, id: string, prefix: string, autocomplete: string, tabindex: string };

const IconCheck = defineAsyncComponent(() =>
  import('@white-label-icon/icon-check')
);





const slots = useSlots();
const proxy = getCurrentInstance()?.proxy as Vue;

// Refs
const localError = ref<string | TranslateResult>('');
const localErrorTimeout = ref<number>(0);

// Computed
const slotExists = computed(() => !!slots?.button);

const isErrorExists = computed(() =>
  (props.error && props.error.$error) || !!localError.value
);

const getErrorLabel = computed(() =>
  props.errorLabel.toLowerCase() || props.label.toLowerCase()
);

const errorMessage = computed(() => {
  if (localError.value) {
    return localError.value;
  }
  if (customErrorMessage.value) {
    return customErrorMessage.value;
  }
  if (!props.error.required) {
    return `${proxy.$t('UI.formErrors.pleaseEnter')} ${getErrorLabel.value}.`;
  }

  return `${proxy.$t('UI.formErrors.pleaseEnterValid')} ${getErrorLabel.value}`;
});

const customErrorMessage = computed(() => {
  const errorState = getErrorState();
  return getErrorMessage(errorState);
});

const successState = computed(() =>
  props.isSuccess && !localError.value
);

// Methods
function onClick(): void {
  if (props.readonly) {
    emit('clickReadonly');
  }
}

function transformUpperCase(value: string): string {
  if (props.inputUpperCase) {
    return value.toUpperCase();
  }
  return value;
}

function transformLowerCase(value: string): string {
  if (props.inputLowerCase) {
    return value.toLowerCase();
  }
  return value;
}

function transformMaxLength(value: string): string {
  if (
    props.maxLength &&
    typeof props.maxLength === 'number' &&
    value.length > props.maxLength
  ) {
    setLocalErrorState(
      proxy.$t('UI.form.global.maxLength', { maxLength: props.maxLength })
    );

    return props.value as string;
  }
  localError.value = '';
  return value;
}

function transformOnlyNumbers(value: string): string {
  if (props.onlyNumbers) {
    return value.replace(/\D+/g, '');
  }
  return value;
}

function onInput(e: Event): void {
  let value = (e.target as HTMLInputElement).value;

  // transform value
  value = transformUpperCase(value);
  value = transformLowerCase(value);
  value = transformMaxLength(value);
  value = transformOnlyNumbers(value);

  emit('input', value);
}

function onFocus(): void {
  emit('focus');
}

function onBlur(): void {
  emit('blur');
}

function onKeyDown(e: KeyboardEvent): void {
  emit('keydown', e);
}

function onPaste(e: ClipboardEvent): void {
  if (props.noPaste) {
    e.preventDefault();
    emit('paste');
  }
}

function setLocalErrorState(newLocalErrorState: string | TranslateResult): void {
  localError.value = newLocalErrorState;

  if (localErrorTimeout.value) {
    clearTimeout(localErrorTimeout.value);
  }
  localErrorTimeout.value = window.setTimeout(() => {
    localError.value = '';
  }, LOCAL_ERROR_SHOW_TIME);
}

function getErrorState(): string {
  if (!props.error.$error) {
    return '';
  }
  const validationFields = Object.entries(props.error).filter(
    (i) => i[0][0] !== '$'
  );
  const foundField = validationFields.find(([, value]) => !value);
  return foundField ? foundField[0] : '';
}

function getErrorMessage(errorName: string): string {
  if (!errorName || !props.customErrorMessages) {
    return '';
  }
  return props.customErrorMessages[errorName];
}

return { __sfc: true,IconCheck, props, emit, slots, proxy, localError, localErrorTimeout, slotExists, isErrorExists, getErrorLabel, errorMessage, customErrorMessage, successState, onClick, transformUpperCase, transformLowerCase, transformMaxLength, transformOnlyNumbers, onInput, onFocus, onBlur, onKeyDown, onPaste, setLocalErrorState, getErrorState, getErrorMessage }
}

})